"use client";

// Дерево документов «Базы знаний» (по parent_id) — ОДНА реализация на весь портал.
//
// Раньше жило внутри виджета на вкладке «Задачи». 11.08.2026 база знаний вернулась
// отдельной вкладкой в «Настройках», и дерево понадобилось во втором месте —
// поэтому вынесено сюда, а не скопировано: разъехавшиеся копии дерева означали бы
// разное поведение раскрытия и разный вид у одних и тех же документов.
//
// Узел = документ: он может быть и страницей, и подразделом (папкой с детьми)
// одновременно, как в Weeek. Направляющая (ствол + локоть + кружок-узел) — в
// стиле бокового меню портала.

import { FileText, Folder } from "lucide-react";
import { IconChevronDown, IconChevronRight, IconPlus } from "@/components/ui/icons";
import { AuthImage } from "@/components/ui/auth-image";
import type { KbDocMeta } from "@/lib/types";

export type DocNode = { doc: KbDocMeta; children: DocNode[] };

export function buildDocTree(items: KbDocMeta[]): DocNode[] {
  const byId = new Map<number, DocNode>();
  for (const d of items) byId.set(d.id, { doc: d, children: [] });
  const roots: DocNode[] = [];
  for (const node of byId.values()) {
    const pid = node.doc.parent_id;
    if (pid != null && byId.has(pid)) byId.get(pid)!.children.push(node);
    else roots.push(node);
  }
  const sortNodes = (arr: DocNode[]) => {
    // папки (с детьми) выше одиночных документов, затем по алфавиту
    arr.sort((a, b) => (b.children.length ? 1 : 0) - (a.children.length ? 1 : 0) || a.doc.title.localeCompare(b.doc.title));
    arr.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

/** Сколько всего документов в поддереве (узел + все потомки). */
export function countDocs(nodes: DocNode[]): number {
  return nodes.reduce((sum, n) => sum + 1 + countDocs(n.children), 0);
}

export function DocTree({ nodes, expanded, onToggle, onOpen, onAddChild, activeId, onDocDragStart, onDocDragEnd }: {
  nodes: DocNode[];
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onOpen: (d: KbDocMeta, rect: DOMRect) => void;
  onAddChild: (parentId: number, rect: DOMRect) => void;
  /** Открытый сейчас документ — подсвечивается (режим «дерево + читалка»). */
  activeId?: number | null;
  onDocDragStart?: (d: KbDocMeta) => void;
  onDocDragEnd?: () => void;
}) {
  return (
    <div className="flex flex-col">
      {nodes.map((n, i) => {
        const hasKids = n.children.length > 0;
        const open = expanded.has(n.doc.id);
        const isLast = i === nodes.length - 1;
        const active = activeId === n.doc.id;
        return (
          <div key={n.doc.id} className="relative">
            {/* ствол вниз к следующему соседу (через детей) — как в главном меню */}
            {!isLast && <span aria-hidden className="pointer-events-none absolute left-[6px] top-[16px] bottom-0 w-px bg-[var(--border)]" />}
            <div className="kb-row group/row relative flex items-center gap-0.5 py-0.5 pl-[20px]">
              {/* «локоть»: вертикаль сверху → плавный заворот к узлу */}
              <span aria-hidden className="pointer-events-none absolute left-[6px] top-0 h-[16px] w-[9px] rounded-bl-[8px] border-b border-l border-[var(--border)]" />
              {/* кружок-узел (заполнен, если раскрыт) */}
              <span aria-hidden className={`pointer-events-none absolute left-[11px] top-[16px] h-[6px] w-[6px] -translate-y-1/2 rounded-full border transition-colors ${open ? "border-[var(--primary)] bg-[var(--primary)]" : "border-[var(--textMuted)] bg-[var(--surface)]"}`} />
              {hasKids ? (
                <button type="button" onClick={() => onToggle(n.doc.id)} style={{ boxShadow: "none" }}
                  className="grid h-5 w-4 shrink-0 place-items-center rounded-[var(--radius-md)] text-[var(--textMuted)] hover:text-[var(--text)]">
                  {open ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
                </button>
              ) : <span className="w-1 shrink-0" />}
              <button type="button" onClick={(e) => onOpen(n.doc, e.currentTarget.getBoundingClientRect())}
                draggable={!!onDocDragStart}
                onDragStart={onDocDragStart ? (e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = "move"; onDocDragStart(n.doc); } : undefined}
                onDragEnd={onDocDragEnd}
                style={active ? { borderColor: "var(--primary)", background: "color-mix(in srgb, var(--primary) 12%, var(--card))" } : undefined}
                className={`glass-flat flex min-w-0 flex-1 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] px-2.5 py-1.5 text-left hover:border-[var(--primary)] ${onDocDragStart ? "cursor-grab active:cursor-grabbing" : ""}`}>
                <span className="grid h-5 w-5 shrink-0 place-items-center text-[12px]">
                  {n.doc.icon_url
                    ? <AuthImage src={n.doc.icon_url} className="h-[15px] w-[15px] object-contain" />
                    : n.doc.emoji
                      ? <span>{n.doc.emoji}</span>
                      : hasKids
                        ? <Folder size={13} className="text-[var(--primary)]" />
                        : <FileText size={13} className="text-[var(--textMuted)]" />}
                </span>
                {/* ⚠️ СТРОКА СПИСКА — 12px, а не заголовочные 16 (Максим, 02.09.2026:
                    «шрифты не соответствуют стандарту»). `.section-title` — стиль
                    ЗАГОЛОВКА блока; на строках дерева он давал перечень из
                    шестнадцатых кеглей, будто каждый документ — заголовок. */}
                <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[var(--text)]">{n.doc.title || "Без названия"}</span>
                {hasKids && <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]">{n.children.length}</span>}
              </button>
              <button type="button" data-tooltip="Добавить документ/подраздел внутри" style={{ boxShadow: "none" }}
                onClick={(e) => onAddChild(n.doc.id, e.currentTarget.getBoundingClientRect())}
                className="kb-row-act glass-flat absolute right-1 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-[var(--radius-md)] text-[var(--textMuted)]">
                <IconPlus size={13} />
              </button>
            </div>
            {hasKids && open && (
              <div className="ml-[12px]">
                <DocTree nodes={n.children} expanded={expanded} onToggle={onToggle} onOpen={onOpen} onAddChild={onAddChild}
                  activeId={activeId} onDocDragStart={onDocDragStart} onDocDragEnd={onDocDragEnd} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
